import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  getEmailList, 
  getEmailListById, 
  createEmailList, 
  updateEmailList, 
  deleteEmailList,
  type EmailListParams,
  type EmailListItem 
} from '../services/emailListService'

// Claves para React Query
export const emailListKeys = {
  all: ['emailList'] as const,
  lists: () => [...emailListKeys.all, 'list'] as const,
  list: (params: EmailListParams) => [...emailListKeys.lists(), params] as const,
  details: () => [...emailListKeys.all, 'detail'] as const,
  detail: (id: number) => [...emailListKeys.details(), id] as const,
}

// Hook para obtener la lista de emails
export const useEmailList = (params: EmailListParams = {}) => {
  return useQuery({
    queryKey: emailListKeys.list(params),
    queryFn: () => getEmailList(params),
    enabled: true,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    retry: (failureCount, error: any) => {
      // No reintentar si es error de autenticación
      if (error?.response?.status === 401) {
        return false
      }
      return failureCount < 3
    },
    refetchOnWindowFocus: false,
  })
}

// Hook para obtener un email específico por ID
export const useEmailListById = (id: number) => {
  return useQuery({
    queryKey: emailListKeys.detail(id),
    queryFn: () => getEmailListById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) {
        return false
      }
      return failureCount < 3
    },
  })
}

// Hook para crear un nuevo email list
export const useCreateEmailList = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createEmailList,
    onSuccess: () => {
      // Invalidar y refetch la lista de emails
      queryClient.invalidateQueries({ queryKey: emailListKeys.lists() })
    },
    onError: (error: any) => {
      console.error('Error creating email list:', error)
    },
  })
}

// Hook para actualizar un email list
export const useUpdateEmailList = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<EmailListItem> }) =>
      updateEmailList(id, data),
    onSuccess: (data, variables) => {
      // Actualizar el cache con los nuevos datos
      queryClient.setQueryData(emailListKeys.detail(variables.id), data)
      // Invalidar la lista para refetch
      queryClient.invalidateQueries({ queryKey: emailListKeys.lists() })
    },
    onError: (error: any) => {
      console.error('Error updating email list:', error)
    },
  })
}

// Hook para eliminar un email list
export const useDeleteEmailList = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteEmailList,
    onSuccess: (_, id) => {
      // Remover del cache
      queryClient.removeQueries({ queryKey: emailListKeys.detail(id) })
      // Invalidar la lista para refetch
      queryClient.invalidateQueries({ queryKey: emailListKeys.lists() })
    },
    onError: (error: any) => {
      console.error('Error deleting email list:', error)
    },
  })
}

// Hook para refrescar la lista de emails
export const useRefreshEmailList = () => {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({ queryKey: emailListKeys.lists() })
  }
}
























